import React, { useState, useEffect } from 'react';
import { X, Save, Stethoscope } from 'lucide-react';
import Select from 'react-select';
import { usePatients, useDoctors, useHospitals } from '../../hooks/useApi';

const ConsultationEditModal = ({ consultation, isOpen, onClose, onSave, loading }) => {
  const { data: patients = [], loading: patientsLoading } = usePatients();
  const { data: doctors = [], loading: doctorsLoading } = useDoctors();
  const { data: hospitals = [], loading: hospitalsLoading } = useHospitals();

  const [formData, setFormData] = useState({
    patientId: null,
    doctorId: null,
    hospitalId: null,
    consultationDate: '',
    chiefComplaint: '',
    diagnosis: '',
    bloodPressure: '',
    heartRate: '',
    temperature: '',
    weight: '',
    height: '',
    status: 'En attente',
    priority: 'Normale',
    followUpRequired: false,
    followUpDate: '',
    notes: ''
  });

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [errors, setErrors] = useState({});

  const patientOptions = patients
    .filter(patient => patient.user)
    .map(patient => ({
      value: patient.id,
      label: `${patient.user.name} - ${patient.age} ans`
    }));

  const doctorOptions = doctors
    .filter(doctor => doctor.user)
    .map(doctor => ({
      value: doctor.id,
      label: `Dr. ${doctor.user.name} - ${doctor.speciality || 'Non spécifié'}`
    }));

  const hospitalOptions = hospitals.map(hospital => ({
    value: hospital.id,
    label: `${hospital.name} - ${hospital.city}`
  }));

  useEffect(() => {
    if (consultation) {
      const consultationDate = new Date(consultation.consultationDate).toISOString().slice(0, 16);
      setFormData({
        patientId: consultation.patientId || null,
        doctorId: consultation.doctorId || null,
        hospitalId: consultation.hospitalId || null,
        consultationDate: consultationDate,
        chiefComplaint: consultation.chiefComplaint || '',
        diagnosis: consultation.diagnosis || '',
        bloodPressure: consultation.bloodPressure || '',
        heartRate: consultation.heartRate || '',
        temperature: consultation.temperature || '',
        weight: consultation.weight || '',
        height: consultation.height || '',
        status: consultation.status || 'En attente',
        priority: consultation.priority || 'Normale',
        followUpRequired: consultation.followUpRequired || false,
        followUpDate: consultation.followUpDate ? new Date(consultation.followUpDate).toISOString().slice(0, 16) : '',
        notes: consultation.notes || ''
      });

      // Définir les sélections
      if (consultation.patientId) {
        const patient = patients.find(p => p.id === consultation.patientId);
        if (patient && patient.user) {
          setSelectedPatient({ value: patient.id, label: `${patient.user.name} - ${patient.age} ans` });
        }
      }
      if (consultation.doctorId) {
        const doctor = doctors.find(d => d.id === consultation.doctorId);
        if (doctor && doctor.user) {
          setSelectedDoctor({ value: doctor.id, label: `Dr. ${doctor.user.name} - ${doctor.speciality || 'Non spécifié'}` });
        }
      }
      if (consultation.hospitalId) {
        const hospital = hospitals.find(h => h.id === consultation.hospitalId);
        if (hospital) {
          setSelectedHospital({ value: hospital.id, label: `${hospital.name} - ${hospital.city}` });
        }
      }
    }
  }, [consultation, patients, doctors, hospitals]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePatientChange = (selectedOption) => {
    setSelectedPatient(selectedOption);
    setFormData(prev => ({ ...prev, patientId: selectedOption ? selectedOption.value : null }));
    if (errors.patientId) setErrors(prev => ({ ...prev, patientId: '' }));
  };

  const handleDoctorChange = (selectedOption) => {
    setSelectedDoctor(selectedOption);
    setFormData(prev => ({ ...prev, doctorId: selectedOption ? selectedOption.value : null }));
    if (errors.doctorId) setErrors(prev => ({ ...prev, doctorId: '' }));
  };

  const handleHospitalChange = (selectedOption) => {
    setSelectedHospital(selectedOption);
    setFormData(prev => ({ ...prev, hospitalId: selectedOption ? selectedOption.value : null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.patientId) newErrors.patientId = 'Le patient est requis';
    if (!formData.doctorId) newErrors.doctorId = 'Le médecin est requis';
    if (!formData.chiefComplaint?.trim()) newErrors.chiefComplaint = 'Le motif de consultation est requis';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm() && consultation) {
      onSave(consultation.id, formData);
    }
  };

  if (!isOpen || !consultation) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-cyan-100 rounded-full">
              <Stethoscope className="h-6 w-6 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Modifier la consultation</h2>
              <p className="text-gray-600">Consultation du {new Date(consultation.consultationDate).toLocaleDateString('fr-FR')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" disabled={loading}>
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Même structure que ConsultationAddModal mais avec les données chargées */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations générales</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Patient *</label>
                <Select value={selectedPatient} onChange={handlePatientChange} options={patientOptions} isLoading={patientsLoading} className="react-select-container" classNamePrefix="react-select" />
                {errors.patientId && <p className="text-red-500 text-sm mt-1">{errors.patientId}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Médecin *</label>
                <Select value={selectedDoctor} onChange={handleDoctorChange} options={doctorOptions} isLoading={doctorsLoading} className="react-select-container" classNamePrefix="react-select" />
                {errors.doctorId && <p className="text-red-500 text-sm mt-1">{errors.doctorId}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hôpital</label>
                <Select value={selectedHospital} onChange={handleHospitalChange} options={hospitalOptions} isLoading={hospitalsLoading} isClearable className="react-select-container" classNamePrefix="react-select" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date et heure *</label>
                <input type="datetime-local" name="consultationDate" value={formData.consultationDate} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Consultation médicale</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Motif de consultation *</label>
                <input type="text" name="chiefComplaint" value={formData.chiefComplaint} onChange={handleChange} className={`w-full px-3 py-2 border rounded-lg ${errors.chiefComplaint ? 'border-red-500' : 'border-gray-300'}`} />
                {errors.chiefComplaint && <p className="text-red-500 text-sm mt-1">{errors.chiefComplaint}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Diagnostic</label>
                <input type="text" name="diagnosis" value={formData.diagnosis} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Signes vitaux</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Tension artérielle</label>
                <input type="text" name="bloodPressure" value={formData.bloodPressure} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Rythme cardiaque</label>
                <input type="number" name="heartRate" value={formData.heartRate} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Température (°C)</label>
                <input type="number" step="0.1" name="temperature" value={formData.temperature} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Poids (kg)</label>
                <input type="number" step="0.1" name="weight" value={formData.weight} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Suivi</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                <select name="status" value={formData.status} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="En attente">En attente</option>
                  <option value="En cours">En cours</option>
                  <option value="Terminée">Terminée</option>
                  <option value="Annulée">Annulée</option>
                  <option value="Reportée">Reportée</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                <select name="priority" value={formData.priority} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="Faible">Faible</option>
                  <option value="Normale">Normale</option>
                  <option value="Élevée">Élevée</option>
                  <option value="Urgente">Urgente</option>
                </select>
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" name="followUpRequired" checked={formData.followUpRequired} onChange={handleChange} className="rounded border-gray-300" />
                  <span className="text-sm text-gray-700">Suivi requis</span>
                </label>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" disabled={loading}>
              Annuler
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center space-x-2 disabled:opacity-50">
              <Save className="h-4 w-4" />
              <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsultationEditModal;
